import api from './client'
import axios from 'axios'
import type { LoginRequest, LoginResponse, TwoFactorSetupResponse, User, UserRole } from '../api'

export async function ensureCsrfCookie(): Promise<void> {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true })
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      await ensureCsrfCookie()
      const response = await api.post<LoginResponse>('/auth/login', credentials)
      return response.data
    } catch (error) {
      if (axios.isAxiosError<LoginResponse>(error) && error.response?.status === 423 && error.response.data?.status === 'needs_2fa_enrollment') {
        return error.response.data as LoginResponse
      }
      throw error
    }
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },

  getUser: async (): Promise<User> => {
    const response = await api.get<User>('/user')
    return response.data
  },
}

export const twoFactorApi = {
  setup: async (): Promise<TwoFactorSetupResponse> => {
    const response = await api.get<TwoFactorSetupResponse>('/me/2fa/setup')
    return response.data
  },

  confirm: async (code: string): Promise<{ message: string; recovery_codes: string[] }> => {
    const response = await api.post<{ message: string; recovery_codes: string[] }>('/me/2fa/confirm', { code })
    return response.data
  },

  verifyLogin: async (challengeToken: string, code: string, rememberDevice = false): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/2fa/verify', {
      challenge_token: challengeToken,
      code,
      remember_device: rememberDevice,
    })
    return response.data
  },

  verifyRecovery: async (challengeToken: string, recoveryCode: string, rememberDevice = false): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/2fa/recovery', {
      challenge_token: challengeToken,
      recovery_code: recoveryCode,
      remember_device: rememberDevice,
    })
    return response.data
  },
}

export const forgotPasswordApi = {
  requestLink: async (email: string): Promise<{ message: string }> => {
    await ensureCsrfCookie()
    const response = await api.post<{ message: string }>('/auth/forgot-password', { email })
    return response.data
  },

  reset: async (
    email: string,
    token: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<{ message: string }> => {
    await ensureCsrfCookie()
    const response = await api.post<{ message: string }>('/auth/forgot-password/reset', {
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
    })
    return response.data
  },
}

export const firstLoginApi = {
  show: async (token: string): Promise<{ email: string; name: string; role: UserRole; expires_at: string | null }> => {
    const response = await api.get(`/auth/first-login/${token}`)
    return response.data
  },

  setPassword: async (token: string, password: string, passwordConfirmation: string): Promise<LoginResponse> => {
    await ensureCsrfCookie()
    const response = await api.post<LoginResponse>('/auth/first-login', {
      token,
      password,
      password_confirmation: passwordConfirmation,
    })
    return response.data
  },
}
